import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDrag, useDrop } from 'react-dnd'
import { TooltipIcon } from '../form/TooltipIcon'
import CellResizer, { headerResizeHandler } from './CellResizer'
import { IColumn, PinnedStatus, onFilterChangeWrapped } from './Types'
import { PopupDropdown } from './Filter/PopupDropdown'
import { KnockOrderByOptions } from './Knock/KnockTypes'

export type onHeaderMove = (source: IColumn, target: IColumn | number, pinnedStatus?: PinnedStatus) => void

export interface HeaderCellProps {
  header: IColumn
  onHeaderMove?: onHeaderMove
  onHeaderResize: headerResizeHandler
  onHeaderResizeCommit: headerResizeHandler
  onFilterChange?: onFilterChangeWrapped

  filter: PinnedStatus
}

export const HeaderCellSource = 'HEADER_CELL_SOURCE'

const nextSortorder = (sortOrder: string) => {
  if (sortOrder === KnockOrderByOptions.None) {
    return KnockOrderByOptions.Ascending
  } else if (sortOrder === KnockOrderByOptions.Ascending) {
    return KnockOrderByOptions.Descending
  }

  return KnockOrderByOptions.None
}

const HeaderCell: React.FC<HeaderCellProps> = props => {
  const { header, onHeaderResize, onHeaderResizeCommit, onHeaderMove, onFilterChange, filter } = props
  const { t } = useTranslation()

  const [, drag] = useDrag(() => ({
    type: HeaderCellSource,
    item: props.header,
    canDrag: !props.header.static
  }))
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: HeaderCellSource,
      drop: (item: IColumn) => {
        if (props.header.pinned !== PinnedStatus.Center && props.filter === PinnedStatus.Center) {
          return
        }

        if (props.header.static) {
          return
        }

        props.onHeaderMove?.(item, props.header)
      },
      collect: monitor => ({
        isOver: monitor.isOver()
      })
    }),
    [props.header, props.onHeaderMove, props.filter]
  )

  const FilterComponent = header.filterComponent
  const isFromPinned = header.pinned !== PinnedStatus.Center && filter === PinnedStatus.Center

  return (
    <th style={{ backgroundColor: isOver ? '#eaf8fe' : 'transparent' }} ref={drop}>
      <div className='cbgms-grid-headernav'>
        <span
          className='cbgms-grid-name'
          style={{ opacity: isFromPinned ? 0 : 1 }}
          onClick={() => !header.static && onFilterChange?.(header, { sortOrder: nextSortorder(header.sortOrder!) })}
          title={header.name}
          ref={drag}
        >
          {header.name}
        </span>

        {!header.static && (
          <>
            <div className='cbgms-grid-header-icons'>
              <TooltipIcon
                title={t('grid.tooltip_sort')}
                name={header.sortOrder === 'DESC' ? 'sort amount up' : 'sort amount down'}
                onClick={() => onFilterChange?.(header, { sortOrder: nextSortorder(header.sortOrder!) })}
                style={{ opacity: header.sortOrder === '' ? 0.3 : 1 }}
              />
              {header.sortOrder !== '' && header.sortIndex}
              <PopupDropdown
                trigger={
                  <TooltipIcon
                    title={t('grid.tooltip_pin')}
                    name='pin'
                    style={{ opacity: header.pinned === PinnedStatus.Center ? 0.3 : 1 }}
                  />
                }
                options={Object.entries(PinnedStatus).map(([key, value]) => ({
                  value,
                  text: t(`grid.${key.toLowerCase()}`),
                  disabled: header.pinned === value
                }))}
                onChange={value => onHeaderMove?.(header, 0, value)}
              />
            </div>
            <CellResizer headerKey={header.key} onResize={onHeaderResize} onResizeCommit={onHeaderResizeCommit} />
          </>
        )}
      </div>

      <div className='cbgms-grid-filter'>
        {FilterComponent && (
          <FilterComponent
            name={header.key}
            filter={header.filter}
            filterOperator={header.filterOperator!}
            onChange={value => onFilterChange?.(header, value)}
          />
        )}
      </div>
    </th>
  )
}

export default HeaderCell
